// Compact cart compact cart drawer (2026-10-28; operator: live for every shopper). Styles: assets/pisr-cart.css.
// Loaded once from layout/theme.liquid as a module (deferred; module scripts run once per URL). Cart drawer only: it
// adds behaviour around the theme's own drawer markup; cart updates, quantities, Remove and checkout stay the theme's
// code. Taken from the cart part of the v3 product page + cart preview (assets/pisr-v3.js there); since 2026-10-29 the
// opening motion no longer replays when the theme re-renders the items or the free-shipping bar, and what it did not
// re-render finishes its own fade (SMR-389); the free-shipping check reads the same element the stylesheet draws it on
// (SMR-388).
const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
const hoverable = matchMedia('(hover: hover)');
const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

// ---------- authenticity tooltip: hover (mouse), tap (touch; a tap elsewhere closes), keyboard focus, Escape ----------
class PisrAuth extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const btn = this.querySelector('.pisr-auth__t');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      // Enter, Space or a screen reader's activation (a click with no pointer behind it). The first activation opens
      // and pins it, and never closes what keyboard focus just opened; the next one closes it, so a screen-reader
      // double-tap toggles. Escape also closes it.
      if (e.detail === 0) {
        if (this.classList.contains('is-open') && this._activated) { this.setOpen(false); return; }
        this.setOpen(true);
        this._pinned = true;
        this._activated = true;
        return;
      }
      // A mouse that already opened it by hovering pins it; a second click (or a tap) toggles.
      if (this.classList.contains('is-open') && !this._pinned && this._byHover) { this._pinned = true; return; }
      this.setOpen(!this.classList.contains('is-open'));
      this._pinned = this.classList.contains('is-open');
    });
    this.addEventListener('mouseenter', () => { if (hoverable.matches && !this.classList.contains('is-open')) { this._byHover = true; this.setOpen(true); } });
    this.addEventListener('mouseleave', () => { if (hoverable.matches && !this._pinned && !btn.matches(':focus-visible')) this.setOpen(false); });
    btn.addEventListener('focus', () => { if (btn.matches(':focus-visible')) this.setOpen(true); });
    btn.addEventListener('blur', () => { if (!this.matches(':hover')) { this._pinned = false; this.setOpen(false); } });
  }
  setOpen(on) {
    this.classList.toggle('is-open', on);
    if (!on) { this._pinned = false; this._byHover = false; this._activated = false; }
  }
  /** Escape belongs to this tooltip only when the shopper is on it: focus inside it, or opened by an activation. */
  ownsEscape() { return this.contains(document.activeElement) || Boolean(this._activated); }
}
define('pisr-auth', PisrAuth);
document.addEventListener('pointerdown', (e) => {
  document.querySelectorAll('pisr-auth.is-open').forEach((a) => { if (!a.contains(e.target)) a.setOpen(false); });
});
// Escape closes any open tooltip. When the shopper is on the tooltip (focus inside it, or opened by an activation) the
// key is theirs for the tooltip alone: the drawer closes on Escape through its focus trap, which listens on the
// document in the capture phase, so this listens one step earlier (the window, capture) and stops the key there. A
// tooltip merely open under the mouse while focus is elsewhere (typing an order note, say) closes and lets the key
// through, so Escape still does what the shopper meant.
window.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  let owned = false;
  document.querySelectorAll('pisr-auth.is-open').forEach((a) => {
    if (a.ownsEscape()) owned = true;
    a.setOpen(false);
  });
  if (!owned) return;
  e.stopPropagation();
  e.preventDefault();
}, true);

// ---------- cart drawer: free-shipping check icon, opening motion ----------
// The drawer element stays in the page; the theme replaces its contents on every cart change, so the observers watch
// the drawer itself.
// The check icon follows the bar's own message, never a sum of ours: the theme decides eligibility (in the shopper's
// currency) and writes its "reached" message into the bar's first span, so the icon shows exactly when that message is
// the one on screen, in any currency or market. MESSAGE is the element pisr-cart.css draws the icon on.
const MESSAGE = ':scope > span:first-child';
// The opening motion: the bar fills and the items fade in once, as the drawer opens (pisr-cart.css, .pisr-opening).
// The theme re-renders the drawer's contents after a cart change (a quantity, Remove, an add from the drawer), and a
// line item or free-shipping bar put in while .pisr-opening is on would start its motion again from nothing: the items
// blink, the bar empties and refills. So each line item or bar a re-render puts in once the motion has begun is marked
// .pisr-placed at once (before the next frame): pisr-cart.css gives it no opening motion, so it simply appears in
// place, while every item the re-render did not replace finishes its own staggered fade (a bar swapped on its own cuts
// no item's fade short). One in the first JOIN_MS still joins the opening, because Add to cart opens the drawer and
// renders the new list in the same instant: until the first item's animation-delay (120ms in pisr-cart.css; the bar's
// is 150ms) has passed nothing has moved yet, so nothing can be seen to restart. A new opening clears the marks.
const OPENING_MS = 1400;
const JOIN_MS = 120;
const RERENDERED = 'line-item, free-shipping-bar';
const words = (s) => String(s || '').replace(/\s+/g, ' ').trim();
const reachedText = (bar) => {
  const t = document.createElement('template');
  t.innerHTML = bar.getAttribute('reached-message') || '';
  return words(t.content.textContent);
};
const place = (n) => {
  if (n.nodeType !== 1) return;
  if (n.matches(RERENDERED)) n.classList.add('pisr-placed');
  n.querySelectorAll(RERENDERED).forEach((el) => el.classList.add('pisr-placed'));
};
function initCartDrawer() {
  const drawer = document.querySelector('cart-drawer.pisr-cart');
  if (!drawer) return;
  const mark = () => {
    drawer.querySelectorAll('free-shipping-bar').forEach((bar) => {
      const want = reachedText(bar);
      const shown = words(bar.querySelector(MESSAGE)?.textContent);
      bar.classList.toggle('pisr-fsb--reached', Boolean(want) && shown === want);
    });
  };
  new MutationObserver(mark).observe(drawer, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['reached-message'] });
  mark();
  if (calm) return;
  let timer = 0;
  let openedAt = -Infinity;
  const stop = () => { clearTimeout(timer); drawer.classList.remove('pisr-opening'); };
  // Only a real opening (the attribute appearing) starts the motion; an open drawer re-setting it does not.
  new MutationObserver((records) => {
    if (!drawer.hasAttribute('open') || !records.some((r) => r.oldValue === null)) return;
    drawer.querySelectorAll('.pisr-placed').forEach((el) => el.classList.remove('pisr-placed'));
    drawer.classList.remove('pisr-opening');
    void drawer.offsetWidth;
    drawer.classList.add('pisr-opening');
    openedAt = performance.now();
    clearTimeout(timer);
    timer = setTimeout(stop, OPENING_MS);
  }).observe(drawer, { attributes: true, attributeFilter: ['open'], attributeOldValue: true });
  new MutationObserver((records) => {
    if (!drawer.classList.contains('pisr-opening') || performance.now() - openedAt < JOIN_MS) return;
    for (const r of records) r.addedNodes.forEach(place);
  }).observe(drawer, { subtree: true, childList: true });
}

initCartDrawer();
